import React, { useEffect, useRef, useCallback, useState } from "react";
import { EventBus } from "../../engine/EventBus.js";
import { WorldStore } from "../../store/WorldStore.js";
import type { Position } from "../../engine/types/grid.js";

const CELL_PX = 5;

/**
 * MiniMap — top-right corner (or inside the desktop panel).
 *
 * Small 5px-per-cell top-down view of the floor with explored cells and a
 * player marker. Read-only HUD; targeting moved to `SpellTargetingBoard`
 * (enemy spells) and `PartyBar` (ally spells) — see specs/combat-spells.md
 * "Wave 2 initiative — battle-board + PartyBar targeting".
 *
 * Dimensions are driven by the loaded map's width/height received via the
 * world:map_name_changed event (W14), so the canvas automatically resizes
 * when moving between floors.
 */
interface MiniMapProps {
  panel?: boolean;
}

export function MiniMap({ panel = false }: MiniMapProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const playerPosRef = useRef<Position>({ x: 0, z: 0 });

  const mapDimsRef = useRef({ width: 10, height: 8 });
  const [mapDims, setMapDims] = useState({ width: 10, height: 8 });
  const [floorName, setFloorName] = useState("");

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const { width, height } = mapDimsRef.current;
    const px = playerPosRef.current;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "rgba(0,0,0,0.8)";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    for (let z = 0; z < height; z++) {
      for (let x = 0; x < width; x++) {
        if (WorldStore.isExplored({ x, z })) {
          ctx.fillStyle = "#4a4a4a";
          ctx.fillRect(x * CELL_PX, z * CELL_PX, CELL_PX - 1, CELL_PX - 1);
        }
      }
    }

    ctx.fillStyle = "#00ccff";
    ctx.fillRect(px.x * CELL_PX, px.z * CELL_PX, CELL_PX - 1, CELL_PX - 1);
  }, []);

  useEffect(() => {
    return EventBus.on("ui:player_moved", ({ position }) => {
      playerPosRef.current = position;
      redraw();
    });
  }, [redraw]);

  useEffect(() => {
    return EventBus.on("world:map_name_changed", ({ displayName, width, height }) => {
      setFloorName(displayName);
      mapDimsRef.current = { width, height };
      setMapDims({ width, height });
    });
  }, []);

  useEffect(() => {
    redraw();
  }, [mapDims, redraw]);

  const wrapStyle: React.CSSProperties = panel
    ? {
        margin: 12,
        alignSelf: "center",
        border: "1px solid rgba(255,255,255,0.25)",
        borderRadius: 3,
        overflow: "hidden",
        background: "rgba(0,0,0,0.6)",
      }
    : {
        position: "absolute",
        top: 12,
        right: 12,
        pointerEvents: "none",
        border: "1px solid rgba(255,255,255,0.25)",
        borderRadius: 3,
        overflow: "hidden",
        background: "rgba(0,0,0,0.6)",
      };

  return (
    <div style={wrapStyle}>
      {floorName && (
        <div
          style={{
            padding: "2px 6px",
            fontSize: 10,
            color: "#ccc",
            textAlign: "center",
            borderBottom: "1px solid rgba(255,255,255,0.15)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {floorName}
        </div>
      )}
      <canvas
        ref={canvasRef}
        width={mapDims.width * CELL_PX}
        height={mapDims.height * CELL_PX}
        style={{ display: "block" }}
      />
    </div>
  );
}
